import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaStar, FaMapMarkerAlt } from 'react-icons/fa';
import { getApiUrl, API_BASE_URL } from '../config/api';
import './FeaturedHotels.css';

const FeaturedHotels = () => {
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Check if user is logged in
  const isUserLoggedIn = () => {
    try {
      const user = localStorage.getItem('user');
      return user !== null;
    } catch {
      return false;
    }
  };

  const handleViewDetails = (hotelId, e) => {
    e.stopPropagation();
    
    if (!isUserLoggedIn()) {
      navigate('/login');
    } else {
      navigate(`/property/${hotelId}`);
    }
  };

  const handleMoreClick = () => {
    navigate('/properties');
  };

  useEffect(() => {
    const fetchFeaturedProperties = async () => {
      try {
        setLoading(true);
        const apiUrl = getApiUrl('/api/properties/featured');
        const baseUrl = API_BASE_URL.replace(/\/$/, '');
        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 Fetching featured properties from:', apiUrl);
        }

        const fetchOptions = {
          method: 'GET',
          headers: { Accept: 'application/json' },
        };
        if (typeof AbortSignal !== 'undefined' && AbortSignal.timeout) {
          fetchOptions.signal = AbortSignal.timeout(15000);
        }
        const response = await fetch(apiUrl, fetchOptions);

        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          setHotels([]);
          return;
        }

        const result = await response.json();
        if (process.env.NODE_ENV === 'development') {
          console.log('📦 Featured API result:', result.success ? result.properties?.length : 0, 'properties');
        }

        if (result.success && result.properties) {
          const transformedHotels = result.properties.slice(0, 3).map((property) => {
            let imageUrl = 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80';
            if (property.images && property.images.length > 0) {
              const firstImage = property.images[0];
              if (firstImage && firstImage.startsWith('/uploads/')) {
                imageUrl = `${baseUrl}${firstImage}`;
              } else if (firstImage) {
                imageUrl = firstImage;
              }
            }
            return {
              id: property._id || property.id,
              _id: property._id,
              image: imageUrl,
              title: property.name || 'Property',
              location: property.city || property.address || 'Location',
              price: property.pricePerNight || 0,
              rating: property.rating || 0,
              reviews: property.reviews || 0,
              property: property,
            };
          });
          setHotels(transformedHotels);
        } else {
          setHotels([]);
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          if (process.env.NODE_ENV === 'development') {
            console.warn('Featured properties request timed out.');
          }
        } else if (error.message === 'Failed to fetch' || error.name === 'TypeError') {
          if (process.env.NODE_ENV === 'development') {
            console.warn(
              'Featured properties: server unreachable. Ensure the backend is running (e.g. port 5000) and proxy is set in client/package.json.'
            );
          }
        } else {
          console.error('Error fetching featured properties:', error);
        }
        setHotels([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedProperties();
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <section className="featured-hotels">
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="section-title">Featured Hotels & Homes</h2>
          <p className="section-subtitle">
            Discover our handpicked selection of premium accommodations
          </p>
        </motion.div>

        <motion.div
          className="hotels-grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {loading ? (
            <div className="empty-state">
              <p>Loading featured properties...</p>
            </div>
          ) : hotels.length === 0 ? (
            <div className="empty-state">
              <p>No featured properties available at the moment.</p>
            </div>
          ) : (
            hotels.map((hotel) => (
              <motion.div
                key={hotel.id}
                className="hotel-card"
                variants={itemVariants}
                whileHover={{ y: -4 }}
                transition={{ duration: 0.3 }}
              >
                <div className="hotel-image-container">
                  <img src={hotel.image} alt={hotel.title} className="hotel-image" />
                  <div className="hotel-overlay" />
                </div>

                <div className="hotel-info">
                  <div className="hotel-header">
                    <h3 className="hotel-title">{hotel.title}</h3>
                  </div>

                  {hotel.rating > 0 && (
                    <div className="hotel-rating">
                      <FaStar className="star-icon" />
                      <span>{hotel.rating.toFixed(1)}</span>
                      {hotel.reviews > 0 && (
                        <span className="reviews-count">({hotel.reviews} reviews)</span>
                      )}
                    </div>
                  )}

                  <div className="hotel-location">
                    <FaMapMarkerAlt className="location-icon" />
                    <span>{hotel.location}</span>
                  </div>

                  <div className="hotel-price">
                    <span className="price-amount">
                      {hotel.price > 0 ? `₹${hotel.price.toLocaleString()}` : 'Price on request'}
                    </span>
                    {hotel.price > 0 && <span className="price-period">/ night</span>}
                  </div>

                  <div className="hotel-actions">
                    <button
                      className="btn-view-details"
                      onClick={(e) => handleViewDetails(hotel._id, e)}
                    >
                     Book Now
                    </button>
                    <button
                      className="btn-book"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (hotel._id) {
                          navigate(`/property/${hotel._id}`);
                        }
                      }}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </motion.div>

        {/* More Button */}
        {!loading && hotels.length > 0 && (
          <motion.div
            className="more-button-container"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <button className="btn-more" onClick={handleMoreClick}>
              View All Accommodations
            </button>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default FeaturedHotels;


